import {
  Box,
  Theme,
  Button,
  lighten,
  useTheme,
  Typography,
} from "@mui/material";

import type { PacksList } from "@/types";

type PackCardProps = {
  pack: PacksList;
  isSelected: boolean;
  onSelect: (packId: number) => void;
};

export function PackCard({ pack, isSelected, onSelect }: PackCardProps) {
  const theme = useTheme();
  const styles = useStyles(theme);

  return (
    <Box sx={styles.packsCardWrapper} className="PacksCardTab__wrapper">
      <Button
        variant="text"
        onClick={() => onSelect(pack.id)}
        sx={{
          ...styles.packsCardButton,
          height: "100%",
          bgcolor: theme.palette.background.paper,
          outline: isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : `1px solid ${theme.palette.divider}`,
          "&:hover": {
            bgcolor: "rgba(0, 56, 49, 0.04)",
            transform: `scale(1.01)`,
            boxShadow: `0 0 0 6px ${lighten(theme.palette.primary.main, 0.8)}`,
            outline: isSelected
              ? `2px solid ${theme.palette.primary.main}`
              : `2px solid ${lighten(theme.palette.primary.main, 0.6)}`,
          },
        }}
      >
        <Typography variant="h6" sx={styles.packsCardButtonTitle}>
          {typeof pack.title === "string" ? (
            <span dangerouslySetInnerHTML={{ __html: pack.title }} />
          ) : (
            pack.title
          )}
        </Typography>

        {pack.description ? (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={styles.packsCardButtonDescription}
          >
            {typeof pack.description === "string" ? (
              <span dangerouslySetInnerHTML={{ __html: pack.description }} />
            ) : (
              pack.description
            )}
          </Typography>
        ) : null}
      </Button>

      <Box
        sx={{
          opacity: 0,
          ...styles.packsCardSelectButtonWrapper,
        }}
        className="PacksCardTab__buttonWrapper"
      >
        <Button
          fullWidth
          size="medium"
          variant="contained"
          sx={styles.packsCardSelectButton}
          onClick={() => onSelect(pack.id)}
        >
          Select +
        </Button>
      </Box>
    </Box>
  );
}

const useStyles = (theme: Theme) => {
  const { palette, breakpoints } = theme;
  return {
    packsCardWrapper: {
      position: "relative",
      width: "100%",
      height: "100%",
      alignSelf: "stretch",

      ":hover": {
        "& .PacksCardTab__buttonWrapper": {
          opacity: 1,
        },
      },
    },

    packsCardButton: {
      p: 4,
      width: "100%",
      display: "flex",
      textAlign: "left",
      borderRadius: "8px",
      flexDirection: "column",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      flexGrow: 1,
      transformOrigin: "top top",
      transition: "all 0.2s ease",

      [breakpoints.down(700)]: {
        p: 1,
      },
    },

    packsCardButtonTitle: {
      fontWeight: 700,
      lineHeight: 1.2,
      fontSize: "1.2rem",
      color: "text.primary",

      [breakpoints.down(700)]: {
        fontSize: "0.875rem",
      },
    },

    packsCardButtonDescription: {
      mt: 0.5,
      [breakpoints.down(1210)]: {
        maxWidth: "300px",
      },

      [breakpoints.down(700)]: {
        fontSize: "0.75rem",
      },
    },

    packsCardSelectButtonWrapper: {
      px: 4,
      bottom: -20,
      width: "100%",
      position: "absolute",
      transition: "all 0.3s ease",

      [breakpoints.down("sm")]: {
        px: 3,
      },

      [breakpoints.down(700)]: {
        display: "none",
      },

      [breakpoints.down(607)]: {
        px: 1,
      },
    },

    packsCardSelectButton: {
      fontSize: "0.9rem",
      [breakpoints.down(495)]: {
        fontSize: "0.75rem",
      },
    },
  };
};
